import { CustomFieldDefinition, ResourceType } from '__types__';
import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import { createMatcher } from '@passionware/query-toolkit';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import store from 'store';
import { RESOURCE_TYPES_ACTIONS } from 'store/v1/resource_types/resource_types.constants.js';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import type { Sorter } from 'v5/api/_common/query/sorters/Sorter';
import { resourceTypeBindingsUtils } from 'v5/api/resource-types/resource-type-bindings.api';
import {
  ResourceTypeApi,
  throwResourceTypesListUnavailable
} from 'v5/api/resource-types/resource-types.api';
import { resourceTypesCacheKeys } from 'v5/api/resource-types/resource-types.cache';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import type { Nullable } from 'v5/platform/typescript';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { ResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import {
  mapResourceTypeToAdmin,
  resolveFieldAssignments
} from './mapResourceTypeFromApi';
import { ResourceTypeAdmin, ResourceTypeListFilter } from './model';
import {
  ResourceTypeAdminService,
  ResourceTypeWorkspaceRow,
  ResourceTypeWorkspacesQuery,
  ResourceTypeWorkspacesSortField,
  WorkspaceResourceTypeDetail
} from './ResourceTypeAdminService';

function sortWorkspaceRows(
  rows: ResourceTypeWorkspaceRow[],
  sort: Nullable<Sorter<ResourceTypeWorkspacesSortField>>
): ResourceTypeWorkspaceRow[] {
  if (sort == null) {
    return rows;
  }
  const dir = sort.order === 'desc' ? -1 : 1;
  return [...rows].sort(
    (a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * dir
  );
}

function applyResourceTypeWorkspacesQuery(
  rows: ResourceTypeWorkspaceRow[],
  query: ResourceTypeWorkspacesQuery
): ResourceTypeWorkspaceRow[] {
  return sortWorkspaceRows(
    rows.filter(createMatcher(query.search, workspace => workspace.name)),
    query.sort
  );
}

function filterTypes(
  types: ResourceTypeAdmin[],
  filter: ResourceTypeListFilter
): ResourceTypeAdmin[] {
  const sorted = [...types].sort((a, b) => a.order - b.order || a.id - b.id);
  if (filter === 'active') return sorted.filter(type => !type.archived);
  if (filter === 'archived') return sorted.filter(type => type.archived);
  return sorted;
}

function definitionsRecord(
  defs: CustomFieldDefinition[] | Record<number, CustomFieldDefinition> | null
): Record<number, CustomFieldDefinition> {
  if (!defs) return {};
  if (Array.isArray(defs)) {
    return Object.fromEntries(defs.map(def => [def.id, def]));
  }
  return defs;
}

/**
 * Read facade: RQ cache seeded from auth/Redux via v1Event.
 * Writes live on {@link import('./ResourceTypeAdminMutationService').ResourceTypeAdminMutationService}.
 */
export function createResourceTypeAdminService(
  config: WithServices<
    [
      WithErrorService,
      WithAppScopeService,
      WithEnterpriseService,
      WithMetastructureMappingService
    ]
  > & {
    api: ResourceTypeApi;
    queryClient: QueryClient;
    resourceTypeBindingsService: ResourceTypeBindingsService;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ResourceTypeAdminService {
  const {
    api,
    queryClient,
    resourceTypeBindingsService,
    v1Event,
    services: {
      errorService,
      appScopeService,
      enterpriseService,
      metastructureMappingService
    }
  } = config;
  const interceptor = errorService.withCategory('ResourceTypeAdminService');

  function readResourceTypesFromRedux(): ResourceType[] {
    const state = store.getState() as {
      resource_types?: {
        data?: Record<number, ResourceType>;
        fetched?: boolean;
      };
    };
    return Object.values(state.resource_types?.data ?? {});
  }

  function seedResourceTypesCache(types: ResourceType[]) {
    queryClient.setQueryData(resourceTypesCacheKeys.list(), types);
    for (const type of types) {
      queryClient.setQueryData(resourceTypesCacheKeys.item(type.id), type);
    }
  }

  function upsertResourceTypeInCache(type: ResourceType) {
    queryClient.setQueryData(resourceTypesCacheKeys.item(type.id), type);
    const list =
      queryClient.getQueryData<ResourceType[]>(resourceTypesCacheKeys.list()) ??
      readResourceTypesFromRedux();
    const next = list.some(existing => existing.id === type.id)
      ? list.map(existing => (existing.id === type.id ? type : existing))
      : [...list, type];
    queryClient.setQueryData(resourceTypesCacheKeys.list(), next);
  }

  function isResourceTypesFetched(): boolean {
    const state = store.getState() as {
      resource_types?: { fetched?: boolean };
    };
    return state.resource_types?.fetched === true;
  }

  // Auth/settings bootstrap already has the full list (including null-archived).
  // GET /resource_types filters with SQL `archived = false` and drops nulls.
  const existingFromAuth = readResourceTypesFromRedux();
  if (existingFromAuth.length > 0) {
    seedResourceTypesCache(existingFromAuth);
  }

  v1Event.addListener(async event => {
    if (event.type === 'LOAD_AUTH_SUCCESS') {
      seedResourceTypesCache(event.result.resource_types ?? []);
      return;
    }

    const type = event.type as string;
    // actionAsEventMiddleware emits before the reducer runs, so do not read Redux
    // here for create/update — use action.result (same pattern as CalloutsService).
    if (
      type === RESOURCE_TYPES_ACTIONS.CREATE_RESOURCE_TYPE_SUCCESS ||
      type === RESOURCE_TYPES_ACTIONS.UPDATE_RESOURCE_TYPE_SUCCESS
    ) {
      const updated = (event as { result?: ResourceType }).result;
      if (updated) {
        upsertResourceTypeInCache(updated);
      }
      return;
    }

    if (type === RESOURCE_TYPES_ACTIONS.UPDATE_RESOURCE_TYPE_ORDER_SUCCESS) {
      const ordered = (event as { result?: ResourceType[] }).result;
      if (ordered) {
        seedResourceTypesCache(ordered);
      }
      return;
    }

    if (type === RESOURCE_TYPES_ACTIONS.DELETE_RESOURCE_TYPE_SUCCESS) {
      // Reducer applies archived flag; wait until after reduce.
      queueMicrotask(() => {
        seedResourceTypesCache(readResourceTypesFromRedux());
      });
    }
  });

  async function fetchList(): Promise<ResourceType[]> {
    const fromRedux = readResourceTypesFromRedux();
    if (fromRedux.length > 0 || isResourceTypesFetched()) {
      seedResourceTypesCache(fromRedux);
      return fromRedux;
    }

    const cached = queryClient.getQueryData<ResourceType[]>(
      resourceTypesCacheKeys.list()
    );
    if (cached) {
      return cached;
    }

    throwResourceTypesListUnavailable();
  }

  function useMappedList(
    filter: ResourceTypeListFilter
  ): RemoteData<ResourceTypeAdmin[]> {
    const listRd = useQuery(
      {
        queryKey: resourceTypesCacheKeys.list(),
        queryFn: fetchList,
        // Auth/settings is the list source of truth; mutations reseed via v1Event.
        staleTime: Infinity
      },
      queryClient
    ) as RemoteData<ResourceType[]>;
    const defsRd = metastructureMappingService.useCustomFieldDefinitions();
    const groupDefsRd =
      metastructureMappingService.useCustomFieldGroupDefinitions();

    return useMemo(
      () =>
        rd.map(
          rd.combine({ types: listRd, defs: defsRd, groupDefs: groupDefsRd }),
          ({ types, defs, groupDefs }) =>
            filterTypes(
              types.map(type =>
                mapResourceTypeToAdmin(
                  type,
                  definitionsRecord(defs),
                  groupDefs ?? {}
                )
              ),
              filter
            )
        ),
      [listRd, defsRd, groupDefsRd, filter]
    );
  }

  function useMappedItem(id: Maybe<number>): RemoteData<ResourceTypeAdmin> {
    const itemRd = ensureIdleQuery(
      id,
      useQuery(
        {
          queryKey: resourceTypesCacheKeys.item(id!),
          enabled: maybe.isPresent(id),
          // Prefer auth/Redux + v1Event reseeds; avoid racing api.get over writes.
          staleTime: Infinity,
          queryFn: interceptor.wrap(async () => {
            if (!maybe.isPresent(id)) {
              throw new Error('Resource type id is required');
            }
            const fromRedux = readResourceTypesFromRedux().find(
              type => type.id === id
            );
            if (fromRedux) {
              queryClient.setQueryData(
                resourceTypesCacheKeys.item(id),
                fromRedux
              );
              return fromRedux;
            }
            const type = await api.get(id);
            queryClient.setQueryData(resourceTypesCacheKeys.item(id), type);
            return type;
          })
        },
        queryClient
      ) as RemoteData<ResourceType>
    );
    const defsRd = metastructureMappingService.useCustomFieldDefinitions();
    const groupDefsRd =
      metastructureMappingService.useCustomFieldGroupDefinitions();

    return useMemo(
      () =>
        rd.map(
          rd.combine({ type: itemRd, defs: defsRd, groupDefs: groupDefsRd }),
          ({ type, defs, groupDefs }) =>
            mapResourceTypeToAdmin(
              type,
              definitionsRecord(defs),
              groupDefs ?? {}
            )
        ),
      [itemRd, defsRd, groupDefsRd]
    );
  }

  function useOrganizationId(): string {
    return appScopeService.useOrganizationId();
  }

  return {
    useResourceTypes: filter => useMappedList(filter),
    useResourceType: id => useMappedItem(id),

    useCustomFieldCatalog() {
      const defsRd = metastructureMappingService.useCustomFieldDefinitions();
      return useMemo(
        () =>
          rd.map(defsRd, defs => {
            const list = Object.values(definitionsRecord(defs));
            return list.filter(def => !def.archived);
          }),
        [defsRd]
      );
    },

    useCustomFieldGroupCatalog() {
      const groupsRd =
        metastructureMappingService.useCustomFieldGroupDefinitions();
      return useMemo(
        () => rd.map(groupsRd, groups => Object.values(groups ?? {})),
        [groupsRd]
      );
    },

    useWorkspaceResourceTypes(workspaceId) {
      const typesRd = useMappedList('active');
      const organizationId = useOrganizationId();
      const bindingsRd = resourceTypeBindingsService.useBindings(
        maybe.of(organizationId)
      );

      return useMemo(() => {
        if (maybe.isAbsent(workspaceId)) {
          return rd.ofIdle();
        }
        return rd.map(
          rd.combine({ types: typesRd, bindings: bindingsRd }),
          ({ types, bindings }) => {
            const active: ResourceTypeAdmin[] = [];
            const inactive: ResourceTypeAdmin[] = [];
            for (const type of types) {
              const binding = resourceTypeBindingsUtils.resolveBinding(
                bindings,
                type.id,
                workspaceId
              );
              if (binding.enabled) {
                active.push(type);
              } else {
                inactive.push(type);
              }
            }
            return { active, inactive };
          }
        );
      }, [typesRd, bindingsRd, workspaceId]);
    },

    useEnabledWorkspaces(resourceTypeId) {
      const organizationId = useOrganizationId();
      const workspacesRd = enterpriseService.useWorkspaces(
        maybe.of(organizationId),
        'active'
      );
      const bindingsRd = resourceTypeBindingsService.useBindings(
        maybe.of(organizationId)
      );

      return useMemo(() => {
        if (maybe.isAbsent(resourceTypeId)) {
          return rd.ofIdle();
        }
        return rd.map(
          rd.combine({ workspaces: workspacesRd, bindings: bindingsRd }),
          ({ workspaces, bindings }) =>
            workspaces.filter(workspace => {
              const binding = resourceTypeBindingsUtils.resolveBinding(
                bindings,
                resourceTypeId,
                workspace.id
              );
              return binding.enabled;
            })
        );
      }, [workspacesRd, bindingsRd, resourceTypeId]);
    },

    useResourceTypeWorkspaceRows(resourceTypeId, query) {
      const organizationId = useOrganizationId();
      const workspacesRd = enterpriseService.useWorkspaces(
        maybe.of(organizationId),
        'active'
      );
      const bindingsRd = resourceTypeBindingsService.useBindings(
        maybe.of(organizationId)
      );

      return useMemo(() => {
        if (maybe.isAbsent(resourceTypeId)) {
          return rd.ofIdle();
        }
        return rd.map(
          rd.combine({ workspaces: workspacesRd, bindings: bindingsRd }),
          ({ workspaces, bindings }) => {
            const rows: ResourceTypeWorkspaceRow[] = workspaces.map(
              workspace => ({
                ...workspace,
                enabledHere: resourceTypeBindingsUtils.resolveBinding(
                  bindings,
                  resourceTypeId,
                  workspace.id
                ).enabled
              })
            );
            return applyResourceTypeWorkspacesQuery(rows, query);
          }
        );
      }, [workspacesRd, bindingsRd, resourceTypeId, query]);
    },

    useWorkspaceResourceTypeDetail(workspaceId, resourceTypeId) {
      const typeRd = useMappedItem(resourceTypeId);
      const organizationId = useOrganizationId();
      const bindingsRd = resourceTypeBindingsService.useBindings(
        maybe.of(organizationId)
      );
      const defsRd = metastructureMappingService.useCustomFieldDefinitions();

      return useMemo(() => {
        if (maybe.isAbsent(workspaceId) || maybe.isAbsent(resourceTypeId)) {
          return rd.ofIdle();
        }
        return rd.map(
          rd.combine({ type: typeRd, bindings: bindingsRd, defs: defsRd }),
          ({ type, bindings, defs }): WorkspaceResourceTypeDetail => {
            const binding = resourceTypeBindingsUtils.resolveBinding(
              bindings,
              type.id,
              workspaceId
            );
            return {
              type,
              enabled: binding.enabled,
              globalFields: type.globalFields,
              workspaceFields: resolveFieldAssignments(
                binding.workspaceFields,
                definitionsRecord(defs)
              )
            };
          }
        );
      }, [typeRd, bindingsRd, defsRd, workspaceId, resourceTypeId]);
    }
  };
}
